/**
 * First-party browser collector (EEL93 errors, EEL98 performance).
 *
 * Reports uncaught errors, unhandled promise rejections, failed Livewire round trips and
 * explicit `window.wofCaptureError()` calls to our own ingest endpoint, so frontend
 * defects land in the same Error Explorer as backend ones.
 *
 * EEL98 added the performance half, which lives in ./performance.js and is installed from
 * here. One entry point rather than two, deliberately: it is one bundle, one config island
 * and one Livewire payload parser, and a page that loads the error collector should never be
 * able to end up without the performance collector or vice versa.
 *
 * Design constraints, all of them consequences of running on every page we serve:
 *
 *   - **It must never break a page.** Every entry point is wrapped, every failure is
 *     swallowed. A telemetry collector that throws is strictly worse than no collector.
 *   - **It must never report itself.** A bug in here would otherwise generate an error per
 *     report, each generating another.
 *   - **It must be cheap when things are going wrong.** A page in a render loop can throw
 *     thousands of times a second; the per-page cap and the dedupe below exist so that
 *     costs a few requests rather than a few thousand.
 *
 * The server does not trust any of these limits — the endpoint enforces its own — but
 * applying them here is what keeps a broken page from flooding the network from the
 * client side, where no server-side limit can help.
 */

import { installPerformance } from './performance.js'

// Unchanged from EEL93 on purpose: the id is read by whatever build of this module a
// visitor's browser has cached, so renaming it would blind every cached page for a token's
// lifetime after the deploy that renamed it.
const CONFIG_ELEMENT_ID = 'wof-error-capture-config'

/** Hard ceiling on reports sent per page load, dedupe misses included. */
const MAX_REPORTS_PER_PAGE = 10

/** Frames past this are dropped; the innermost ones identify the defect. */
const MAX_FRAMES = 30

/** Matches the server's own truncation, so we do not ship bytes it will discard. */
const MAX_MESSAGE_LENGTH = 512

/** Key names past this are elided from a non-Error description; the first few identify it. */
const MAX_DESCRIBED_KEYS = 8

/**
 * Chrome/Edge/Firefox stack line, in the two shapes V8 and SpiderMonkey emit:
 *   "    at doThing (https://host/build/assets/app-abc.js:1:2345)"
 *   "    at https://host/build/assets/app-abc.js:1:2345"
 * The function name is optional because anonymous frames are common in bundled code.
 */
const V8_FRAME = /^\s*at\s+(?:(.+?)\s+\()?(.+?):(\d+):(\d+)\)?\s*$/

/**
 * Safari/Firefox stack line:
 *   "doThing@https://host/build/assets/app-abc.js:1:2345"
 * Note these engines put the function name before an '@' rather than after 'at'.
 */
const SAFARI_FRAME = /^\s*(?:(.*?)@)?(.+?):(\d+):(\d+)\s*$/

let config = null
let sent = 0
let reporting = false
const seen = new Set()

function readConfig() {
    const element = document.getElementById(CONFIG_ELEMENT_ID)

    if (!element) {
        return null
    }

    try {
        return JSON.parse(element.textContent)
    } catch (e) {
        return null
    }
}

/**
 * Parse an Error.stack into frames, innermost first.
 *
 * Stack formats are not standardised, so this recognises the two families and silently
 * drops any line matching neither. A partially parsed stack is still useful; refusing to
 * parse an unfamiliar line would throw away the frames around it.
 */
function parseStack(stack) {
    if (typeof stack !== 'string' || stack === '') {
        return []
    }

    const frames = []

    for (const line of stack.split('\n')) {
        const match = V8_FRAME.exec(line) || SAFARI_FRAME.exec(line)

        if (!match) {
            continue
        }

        const [, fn, file, lineNumber, column] = match

        // The first line of a V8 stack is the message ("TypeError: x is not a function"),
        // which can match the Safari pattern by accident. A frame without a URL-ish file
        // is not a frame.
        if (!file || !/^[a-z]+:\/\//i.test(file)) {
            continue
        }

        frames.push({
            file,
            line: Number(lineNumber),
            column: Number(column),
            function: fn || null,
        })

        if (frames.length >= MAX_FRAMES) {
            break
        }
    }

    return frames
}

/**
 * One frame built from an error event's filename/lineno/colno, for errors with no stack.
 *
 * A script that fails to *parse* throws a SyntaxError whose stack is empty, so without this
 * the report arrives with no frames and the server has nothing to name a culprit with — the
 * whole reason EEL379 exists. The event's location is the browser's own statement of which
 * script failed, and the server already resolves, collapses and fingerprints a frame like
 * this exactly as it would one parsed from a stack.
 *
 * Only a URL-shaped filename counts, by the same test parseStack applies. A redacted
 * cross-origin "Script error." reports an empty filename and line 0, and must keep arriving
 * frameless so the server's opaque-error noise rule still recognises it.
 */
function frameFromLocation(filename, lineno, colno) {
    if (typeof filename !== 'string' || !/^[a-z]+:\/\//i.test(filename)) {
        return []
    }

    return [{
        // An inline script reports the document URL as its filename, query and all — the same
        // magic-link tokens and emails pageUrl() keeps off the wire — so it is stripped here too.
        file: filename.split(/[?#]/)[0],
        line: Number(lineno) || null,
        column: Number(colno) || null,
        function: null,
    }]
}

/**
 * Page URL with the query string and fragment removed.
 *
 * Stripped here as well as on the server because the query is where identifying data
 * lives (a magic-link token, an email in a callback), and the less of it that crosses the
 * network the better. The server strip is the one that has to hold; this one means the
 * value is never sent in the first place.
 */
function pageUrl() {
    return window.location.origin + window.location.pathname
}

/**
 * Collapse repeats of the same defect within one page load.
 *
 * A single broken component re-rendering in a loop produces thousands of identical
 * errors. Keyed on name + message + innermost frame, which is the client-side
 * approximation of the server's fingerprint.
 */
function isDuplicate(report) {
    const first = report.frames[0]
    const key = [
        report.name,
        report.message,
        first ? `${first.file}:${first.line}:${first.column}` : '',
    ].join('|')

    if (seen.has(key)) {
        return true
    }

    seen.add(key)

    return false
}

function send(report) {
    if (!config || sent >= MAX_REPORTS_PER_PAGE || isDuplicate(report)) {
        return
    }

    sent++

    try {
        fetch(config.endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'X-Wof-Error-Token': config.token,
            },
            body: JSON.stringify(report),
            // The page is frequently navigating away at exactly the moment an error is
            // reported; without this the request is cancelled with the document.
            keepalive: true,
            // Never send cookies. The endpoint is unauthenticated by design and the report
            // carries no user identity, so attaching a session to it would add a CSRF
            // surface and a privacy question for no gain.
            credentials: 'omit',
        }).catch(function () {
            // Silent: a failed error report must not produce another error, and a console
            // message here would be noise on every offline or ad-blocked page load.
        })
    } catch (e) {
        // Same reasoning — fetch itself can throw synchronously on a malformed request.
    }
}

/**
 * Describe a non-Error object by its type and key names, e.g. `Object {isFromCancelledTransition}`.
 *
 * `String()` turns every plain object into `[object Object]`, which merges unrelated
 * rejections into one issue and names none of them (EFG301). Key names identify the source;
 * values are left out because a rejected response body can carry personal data. Anything
 * that is not an object is returned unchanged for the caller's own fallback.
 */
function describeNonError(value) {
    if (value === null || typeof value !== 'object' || value instanceof Error) {
        return value
    }

    try {
        const keys = Object.keys(value)
        const type = (value.constructor && value.constructor.name) || 'Object'
        const shown = keys.slice(0, MAX_DESCRIBED_KEYS).join(', ')

        return `${type} {${shown}${keys.length > MAX_DESCRIBED_KEYS ? ', …' : ''}}`
    } catch (e) {
        // A hostile Proxy can throw from ownKeys; the collector must not.
        return 'Object'
    }
}

/**
 * Normalise anything throwable-ish into the ingest payload.
 *
 * `error` may be an Error, a rejected promise's non-Error reason (strings and plain
 * objects are common), or nothing at all when the browser redacted a cross-origin throw.
 * The head snippet passes a non-Error object rejection reason as `extra.reason`.
 */
function report(source, error, extra) {
    // Guards against the collector reporting its own failures — see the module docblock.
    if (reporting) {
        return
    }

    reporting = true

    try {
        const isError = error instanceof Error
        const parsed = parseStack(isError ? error.stack : extra && extra.stack)

        send({
            name: (isError && error.name) || (extra && extra.name) || 'Error',
            message: String((isError && error.message) || (extra && extra.message)
                || describeNonError((extra && extra.reason) || error) || '')
                .slice(0, MAX_MESSAGE_LENGTH),
            source,
            page: pageUrl(),
            occurred_at: new Date().toISOString(),
            // A parsed stack always wins; the event location only fills the gap it leaves.
            frames: parsed.length > 0
                ? parsed
                : frameFromLocation(extra && extra.filename, extra && extra.lineno, extra && extra.colno),
            livewire_component: (extra && extra.livewire_component) || null,
        })
    } catch (e) {
        // Nothing left to do: reporting the reporting failure is exactly the loop this
        // guard exists to prevent.
    } finally {
        reporting = false
    }
}

/**
 * Drain the pre-boot buffer filled by the inline snippet in the head, then keep feeding
 * from it.
 *
 * This module is a deferred ES module, so anything thrown before it evaluates — a syntax
 * error in another bundle, a third-party tag failing during load — has already happened by
 * now. Those are among the most valuable errors to catch and the easiest to miss.
 *
 * The inline snippet's listeners stay registered and become this module's only source of
 * uncaught errors. Registering a second pair here would send every error down two paths;
 * the dedupe would collapse them, but only by accident of ordering, and it would burn a
 * dedupe slot per error to do it.
 */
function drainPreBootQueue() {
    const queued = window.__wofErrorQueue || []

    while (queued.length > 0) {
        const entry = queued.shift()

        // The entry is already the `extra` shape report() reads (name, message, stack, and the
        // event location), so it is passed through whole rather than copied field by field.
        report(entry.source, null, entry)
    }

    // Replace the buffer with a live forwarder, so the inline handlers registered in the
    // head keep working and now report immediately instead of accumulating.
    window.__wofErrorQueue = {
        push(entry) {
            report(entry.source, null, entry)
        },
        get length() {
            return 0
        },
    }
}

/**
 * Extract the component name(s) from a Livewire request body.
 *
 * The outgoing body is `{_token, components: [{snapshot, updates, calls}]}` where each
 * `snapshot` is itself a JSON string carrying `memo.name`. Knowing *which* component
 * failed is the difference between an actionable report and "something Livewire broke",
 * so it is worth the double parse — done defensively, since the payload shape belongs to
 * Livewire and a version bump may change it.
 */
function livewireComponentsFrom(payload) {
    try {
        const body = typeof payload === 'string' ? JSON.parse(payload) : payload
        const components = (body && body.components) || []

        const names = components
            .map(function (component) {
                try {
                    return JSON.parse(component.snapshot).memo.name
                } catch (e) {
                    return null
                }
            })
            .filter(Boolean)

        return names.length > 0 ? names.join(',').slice(0, 255) : null
    } catch (e) {
        return null
    }
}

/**
 * Report failed Livewire round trips.
 *
 * A Livewire failure is a backend defect surfacing in the frontend — the server threw, the
 * component is now wedged, and the user sees a page that has stopped responding. The
 * backend capture records the throw itself; this records that a user was actually sitting
 * in front of it, and which component they were using.
 *
 * `Livewire.hook('request', …)` is the documented v4 hook and is what the shipped build
 * exposes (dist/livewire.esm.js registers legacy request-event support). Registered on
 * `livewire:init` because Livewire is not guaranteed to exist when this module evaluates.
 */
function installLivewireHook() {
    document.addEventListener('livewire:init', function () {
        try {
            window.Livewire.hook('request', function ({ payload, fail }) {
                fail(function ({ status }) {
                    // 419 is an expired session, not a defect: Livewire's own handler shows
                    // the page-expired dialog and the user refreshes. Reporting it would
                    // drown the real 500s.
                    if (status === 419) {
                        return
                    }

                    report('livewire', null, {
                        name: 'LivewireRequestFailed',
                        message: 'Livewire request failed with status ' + status,
                        stack: null,
                        livewire_component: livewireComponentsFrom(payload),
                    })
                })
            })
        } catch (e) {
            // Livewire's hook API changing shape must not take the rest of the collector
            // down with it.
        }
    })
}

function install() {
    config = readConfig()

    if (!config || !config.endpoint || !config.token) {
        return
    }

    /**
     * Explicit capture, for code that handles an error but still wants it recorded —
     * a caught fetch failure in a checkout flow, a validation path that should never
     * have been reachable.
     */
    window.wofCaptureError = function (error, context) {
        report('manual', error, context || {})
    }

    installLivewireHook()
    drainPreBootQueue()

    // Last, and given the same parsed config: the performance collector's own sampling gate
    // decides whether it does anything at all, and error capture must be installed either
    // way. `livewireComponentsFrom` is shared rather than reimplemented so both halves name
    // a component the same way.
    installPerformance(config, livewireComponentsFrom)
}

install()
